import type { NetworkPayload } from "../../../composites/request/request.types";

export type CreateCollectPaymentRequest = {
  amount: number;
  institution_account: API.AccountId;
  transaction_type: "debit";
  ach_company: API.ACHCompanyId;
  company_name: string;
  company_id: string;
  std_ent_cls_code: string;
  entry_desc: string;
  recipient: API.RecipientId;
  description?: string;
  memo?: string;
  device_id?: string;
  device_request_id?: string;
};

export type CreateCollectPaymentResponse = {
  id: string;
  amount: number;
  company_id: API.ACHCompanyId;
  company_name: string;
  created_at: string;
  description: string;
  display_state: string;
  entry_desc: string;
  external_account_details?: API.ACHDestinationId;
  institution_account: API.AccountId;
  processed_at: string | null;
  state: string;
  std_ent_cls_code: string;
  updated_at: string;
  recipient: API.RecipientId;
  memo: string;
  transaction_type: "debit";
};

const isScheduledPayment = (
  values: API.CreateACHPayment.Request | API.CreateScheduledACHPayment.Request,
): values is API.CreateScheduledACHPayment.Request => {
  return "transaction_type" in values && "recurring_rule" in values;
};

export const API = {
  createACHPayment: (
    idempotencyKey: UUID,
    values:
      | API.CreateACHPayment.Request
      | API.CreateScheduledACHPayment.Request,
  ): NetworkPayload => {
    const {
      amount,
      institution_account,
      transaction_type,
      ach_company,
      std_ent_cls_code,
      entry_desc,
      recipient,
      description,
      memo,
      device_id,
      device_request_id,
    } = values;

    const createPaymentPayload = {
      amount,
      institution_account,
      transaction_type,
      ach_company,
      std_ent_cls_code,
      entry_desc,
      recipient,
      description,
      memo,
      device_id,
      device_request_id,
    };

    if (isScheduledPayment(values)) {
      return {
        url: "unverified_ach_transactions/scheduled",
        options: {
          method: "POST",
          payload: {
            ...createPaymentPayload,
            transaction_type: values.transaction_type,
            recurring_rule: values.recurring_rule,
          },
          idempotencyKey,
        },
      };
    }

    return {
      url: "unverified_ach_transactions/",
      options: {
        method: "POST",
        payload: createPaymentPayload,
        idempotencyKey,
      },
    };
  },

  deleteScheduledACHPayment: (
    uuid: API.ScheduledACHPaymentId,
  ): NetworkPayload => {
    return {
      url: `unverified_ach_transactions/scheduled/${uuid}`,
      options: {
        method: "DELETE",
      },
    };
  },

  createCollectPayment: (
    idempotencyKey: UUID,
    request: CreateCollectPaymentRequest,
  ): NetworkPayload => ({
    url: "unverified_ach_transactions",
    options: {
      method: "POST",
      payload: request,
      idempotencyKey,
    },
  }),
};
